import React, { useState } from 'react';

import SPAButton from '@/components/admin/SysadminSPA/shared/SPAButton';
import cn from '@/utils/cn';

import { toneText, toneTint } from './tone';
import { DetectionReport, FixItem } from './types';

const pagePrefix = (item: FixItem) => (item.page ? `Page ${item.page} — ` : '');

const clipboardText = (fixes: DetectionReport['fixes']) => [
  `Print file check — grade ${fixes.grade}`,
  ...fixes.items.map((item, i) => `${i + 1}. ${pagePrefix(item)}${item.title}. ${item.detail}`),
  ...fixes.clean.map(note => `✓ ${note}`),
].join('\n');

const BADGE = 'tw-flex tw-h-6 tw-w-6 tw-flex-none tw-items-center tw-justify-center tw-rounded-full '
  + 'tw-text-xs tw-font-bold';

const FixRow = ({ index, item }: { index: number; item: FixItem }) => (
  <li className="tw-flex tw-gap-3">
    <span className={cn(BADGE, toneTint.warning, toneText.warning)}>{index + 1}</span>
    <div>
      <p className="tw-m-0 tw-text-sm tw-font-bold tw-text-spa-heading">{pagePrefix(item)}{item.title}.</p>
      <p className="tw-m-0 tw-text-sm tw-text-spa-secondary">{item.detail}</p>
    </div>
  </li>
);

const CleanRow = ({ note }: { note: string }) => (
  <li className="tw-flex tw-gap-3 tw-opacity-80">
    <span className={cn(BADGE, toneTint.success, toneText.success)}>✓</span>
    <p className="tw-m-0 tw-text-sm tw-text-spa-secondary">{note}</p>
  </li>
);

// What stands between this file and a fully automatic pour — page numbers, one item per line.
// Serves both directions: fixes to send upstream to whoever owns the InDesign file (via the
// copy button, as plain text for email/Slack) and a heads-up for the sysadmin importing it.
const FixList = ({ fixes }: { fixes: DetectionReport['fixes'] }) => {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    await navigator.clipboard?.writeText(clipboardText(fixes));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const count = fixes.items.length;
  const summary = count === 0
    ? 'Nothing to fix in the InDesign file — it pours cleanly.'
    : `${count} fix${count === 1 ? '' : 'es'} in the InDesign file would make it pour cleanly.`;

  return (
    <div className="tw-mt-3">
      <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-3">
        <div className="tw-flex tw-items-baseline tw-gap-3">
          <span className={cn('tw-text-xl tw-font-bold', count === 0 ? toneText.success : toneText.warning)}>
            {fixes.grade}
          </span>
          <span className="tw-text-sm tw-text-spa-secondary">{summary}</span>
        </div>
        <SPAButton variant="outline" onClick={copy}>{copied ? 'Copied!' : 'Copy report'}</SPAButton>
      </div>

      <ul className="tw-mt-3 tw-flex tw-list-none tw-flex-col tw-gap-2.5 tw-p-0">
        {fixes.items.map((item, i) => <FixRow key={item.title + String(item.page)} index={i} item={item} />)}
        {fixes.clean.map(note => <CleanRow key={note} note={note} />)}
      </ul>
    </div>
  );
};

export default FixList;
